import React, { useEffect } from "react";
import { DisplayFilterProps, FiltersPlugin } from "@me8eon/filters";
import { useTranslation } from "@me8eon/translation";
import { NameAnd } from "@me8eon/record_utils";
import { EonUiRadioButton, EonUiRadioButtonGroup } from "@eon-ui/eon-ui-components-react";
import { EonUiRadioButtonGroupCustomEvent } from "@eon-ui/eon-ui-components";
import { useGlobalAggregations } from "@me8eon/all_data_sources";
import { useSelectedDataView } from "@me8eon/data_view_filter";

export const radioFilterName = "radio";


/**
 * Represents the radio filter plugin configuration for the FiltersPlugin system.
 *
 * @typeParam NameAnd<T> - A generic type representing a name-value pair where the value is of type `T`.
 * @typeParam string - The type of the filter value, which is a string in this case.
 *
 * @property plugin - Specifies the type of plugin, which is "filter".
 * @property type - The unique name or identifier for the radio filter.
 * @property DefaultDisplay - The React component used to render the default display for the radio filter.
 */
export const radioFilterPlugin: FiltersPlugin<NameAnd<string>, string> = {
    plugin: "filter",
    type: radioFilterName,
    changeCausesSubmit: true,
    deleteWhenClean: true,
    DefaultDisplay: RadioFilterDisplay,
};


/**
 * Component to display an radio filter dropdown.
 *
 * @param {DisplayFilterProps<string>} props - The props for the component.
 * @param {Record<string, any>} props.filterOps - The filter operations available for the dropdown.
 * @param {string} props.rootId - The unique identifier for the root element of the dropdown.
 * @param {string} props.value - The current value of the filter.
 *
 * @returns {JSX.Element} A dropdown component for selecting radio filter options.
 *
 * @remarks
 * - The component uses `useGuiFilters` to manage the state of GUI filters.
 * - It fetches global aggregations using `useGlobalAggregations`.
 * - The dropdown options are dynamically generated based on the aggregation data.
 * - The `onValueChanged` handler updates the GUI filters and triggers a search.
 * - The `onDropdownOpened` and `onDropdownClosed` handlers manage the dropdown's z-index styling.
 */
export function RadioFilterDisplay({ filterOps, rootId, filterName, value }: DisplayFilterProps<string>) {

    const [filterValue, setFilterValue] = filterOps;
    const globalAggregates = useGlobalAggregations();
    const agg = filterName && value ? globalAggregates[value] : {};
    const selectedDataView = useSelectedDataView();

    const sortOrderFromConfig = selectedDataView.filterSortOrder?.[value ?? ""];

    const rawOptions = Object.entries(agg || {});
    const options = sortOrderFromConfig
        ? rawOptions
            .filter(([item]) => sortOrderFromConfig.includes(item))
            .sort(([item1], [item2]) => {
                return sortOrderFromConfig.indexOf(item1) - sortOrderFromConfig.indexOf(item2);
            })
        : rawOptions;

    const t = useTranslation();
    const defaultValue = !filterValue ? selectedDataView.default?.[value ?? ""] ?? null : null;
    const size = selectedDataView.size ?? "normal";

    useEffect(() => {
        if (defaultValue) setFilterValue(defaultValue);
    });

    return options.length > 0 ? (
        <EonUiRadioButtonGroup
            data-testid={`radio-${filterName}`}
            onValueChanged={(e: EonUiRadioButtonGroupCustomEvent<string>) => {
                setFilterValue(e.target.value);
            }}
            value={filterValue ?? defaultValue}
            key={filterValue}
            name={`radio_filter_${filterName}`}
            size={size}
            label={t(`filterPage.filterMenu.${value}`)}
        >
            
        {options.map(([item, count], index) => {
                const isSelected = item === defaultValue;
                return (
                    <EonUiRadioButton
                        key={item}
                        initiallyChecked={isSelected}
                        value={item}
                        label={item}
                    />
                );
            })}
        </EonUiRadioButtonGroup>
    ) : null;
}